// ReceiveTab — 接收 Tab
// 复刻 LocalSend receive_tab.dart：居中 logo + 别名 + 四个连接按钮
// 在快船中增加四种连接方式入口

import { useState } from 'react';
import { Logo } from '../common/Logo';
import { QrDisplay } from '../connection/QrDisplay';
import { PairingCodeDisplay } from '../connection/PairingCodeDisplay';
import { LinkShareBox } from '../connection/LinkShareBox';
import { useSettingsStore } from '../../store/settingsStore';
import { useToastStore } from '../common/Toast';
import { createRoom } from '../../lib/api';
import type { RoomCreateResponse } from '@kuaichuan/shared';

interface ReceiveTabProps {
  onRoomCreated: (roomId: string) => void;
  isAuthenticated: boolean;
  onRequireLogin: () => void;
}

export function ReceiveTab({ onRoomCreated, isAuthenticated, onRequireLogin }: ReceiveTabProps) {
  const { alias } = useSettingsStore();
  const toast = useToastStore((s) => s.show);
  const [mode, setMode] = useState<'menu' | 'qr' | 'pairing' | 'link' | 'account'>('menu');
  const [room, setRoom] = useState<(RoomCreateResponse & { pin?: string }) | null>(null);
  const [creating, setCreating] = useState(false);

  const handleCreate = async (createMode: 'qr' | 'pairing' | 'link' | 'account') => {
    // 同账号模式需先登录
    if (createMode === 'account' && !isAuthenticated) {
      onRequireLogin();
      return;
    }
    setCreating(true);
    try {
      const r = await createRoom({ mode: createMode, alias });
      setRoom(r);
      setMode(createMode);
      onRoomCreated(r.roomId);
      toast('房间已创建', 'success');
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-full flex flex-col items-center justify-center p-6 md:p-10">
      <div className="text-center mb-8">
        <Logo size="xl" className="mb-4" />
        <h2 className="text-2xl font-bold text-ink mb-1">{alias}</h2>
        <p className="text-sm text-muted">等待其他设备连接…</p>
      </div>

      {mode === 'menu' && (
        <div className="grid grid-cols-2 gap-3 max-w-md w-full">
          <ConnectionOption
            icon="📷"
            title="二维码"
            desc="扫码连接"
            onClick={() => handleCreate('qr')}
            loading={creating}
          />
          <ConnectionOption
            icon="🔢"
            title="配对码"
            desc="输入 6 位码"
            onClick={() => handleCreate('pairing')}
            loading={creating}
          />
          <ConnectionOption
            icon="🔗"
            title="链接"
            desc="复制链接分享"
            onClick={() => handleCreate('link')}
            loading={creating}
          />
          <ConnectionOption
            icon="👤"
            title="同账号"
            desc={isAuthenticated ? '已登录，自动发现' : '登录后自动发现'}
            onClick={() => handleCreate('account')}
            loading={creating}
          />
        </div>
      )}

      {mode === 'account' && room && (
        <div className="card p-6 max-w-sm w-full text-center animate-slide-up">
          <div className="text-5xl mb-3">🤝</div>
          <h3 className="text-lg font-bold text-ink mb-1">同账号发现已开启</h3>
          <p className="text-xs text-muted mb-4">同一账号的其他设备会自动出现在发送页设备列表</p>
          <div className="rounded-md bg-bg px-4 py-3 text-sm text-muted">
            等待设备加入…
          </div>
          <button type="button" onClick={() => setMode('menu')} className="btn-ghost w-full mt-4">
            返回
          </button>
        </div>
      )}

      {mode === 'qr' && room && (
        <QrDisplay
          url={room.url}
          pin={room.hasPin ? room.pin : undefined}
          onClose={() => setMode('menu')}
        />
      )}

      {mode === 'pairing' && room && room.code && (
        <PairingCodeDisplay
          code={room.code}
          pin={room.hasPin ? room.pin : undefined}
          onClose={() => setMode('menu')}
        />
      )}

      {mode === 'link' && room && (
        <LinkShareBox url={room.url} pin={room.hasPin ? room.pin : undefined} onClose={() => setMode('menu')} />
      )}
    </div>
  );
}

interface ConnectionOptionProps {
  icon: string;
  title: string;
  desc: string;
  onClick: () => void;
  loading?: boolean;
}

function ConnectionOption({ icon, title, desc, onClick, loading }: ConnectionOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="card p-5 flex flex-col items-center gap-2 hover:border-primary hover:shadow-pop transition-all active:scale-95 disabled:opacity-50"
    >
      <span className="text-4xl">{loading ? '⏳' : icon}</span>
      <span className="font-medium text-ink">{title}</span>
      <span className="text-xs text-muted">{desc}</span>
    </button>
  );
}
